import React, {
  useEffect,
  RefObject,
  ReactNode,
  useRef,
  forwardRef,
} from 'react';
import styled from 'styled-components';
import {
  composeRefs,
} from '@amzn/storm-ui-utils';
import { TransitionStatus } from 'react-transition-group';
import CloseButton, { CloseButtonProps } from '../Button/CloseButton/CloseButton';
import DrawerSurface from './DrawerSurface';
import { createStormTaktId, withTaktFallbackId } from '../TaktIdContext';

/**
 * Position the close button and ensure RTL is handled.
 */
export const PositionedCloseButton = styled(CloseButton)`
  position: absolute;

  /* @noflip */ left: auto;
  inset-inline-start: auto;
  /* @noflip */ right: ${({ theme }) => theme.spacing.base};
  inset-inline-end: ${({ theme }) => theme.spacing.base};

  [dir="rtl"] && {
    /* @noflip */ right: auto;
    inset-inline-start: auto;
    /* @noflip */ left: ${({ theme }) => theme.spacing.base};
    inset-inline-end: ${({ theme }) => theme.spacing.base};
  }
`;

export function createSetBottomToVisualViewport(divRef: RefObject<HTMLDivElement>): () => void {
  return function setBottomToVisualViewport(): void {
    if (divRef.current instanceof HTMLElement && window.visualViewport) {
      const { height: viewportHeight, offsetTop: viewportOffsetTop } = window.visualViewport;
      const { innerHeight } = window;

      const divStyle = divRef.current.style;
      // if the viewport is zoomed in (pinch on mobile or trackpad)
      if (innerHeight > viewportHeight) {
        /**
         * set the bottom offset to the window height minus the
         * combined height of the current viewport and its top offset
        */
        divStyle.setProperty('bottom', `${innerHeight - viewportHeight - viewportOffsetTop}px`);
        return;
      }
      divStyle.removeProperty('bottom');
    }
  };
}

export interface OverlayProps {
  children: ReactNode;
  slideState: TransitionStatus;
  handleCloseButtonClicked: (event: unknown) => void;
  closeButtonLabel?: string;
  closeButtonProps?: Record<string, unknown>;
  withCloseButton?: boolean;
  drawerContainerRef: RefObject<HTMLDivElement>;
}

const DrawerCloseButton = withTaktFallbackId<CloseButtonProps>(PositionedCloseButton);

export const Overlay = forwardRef<HTMLDivElement, OverlayProps>(({
  slideState,
  withCloseButton = true,
  closeButtonLabel = '',
  handleCloseButtonClicked,
  closeButtonProps = {},
  children,
  drawerContainerRef,
  ...rest
},
ref) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
      const { visualViewport } = window;

      const setBottomToVisualViewport = createSetBottomToVisualViewport(containerRef);
      visualViewport.addEventListener('resize', setBottomToVisualViewport);
      visualViewport.addEventListener('scroll', setBottomToVisualViewport);
      setBottomToVisualViewport();

      return () => {
        visualViewport.removeEventListener('resize', setBottomToVisualViewport);
        visualViewport.removeEventListener('scroll', setBottomToVisualViewport);
      };
    }
  }, [containerRef]);

  return (
    <DrawerSurface
      {...rest}
      ref={composeRefs(containerRef, ref, drawerContainerRef)}
      slideState={slideState}
      withCloseButton={withCloseButton}
    >
      {withCloseButton && (
        <DrawerCloseButton
          size="lg"
          label={closeButtonLabel}
          onClick={handleCloseButtonClicked}
          taktFallbackId={createStormTaktId('drawer-close-button')}
          {...closeButtonProps}
        />
      )}
      {children}
    </DrawerSurface>
  );
});

Overlay.displayName = 'Overlay';
Overlay.defaultProps = {
  closeButtonLabel: '',
  closeButtonProps: undefined,
  withCloseButton: true,
};
